import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, addDoc, query, orderBy, Timestamp, deleteDoc, doc, setDoc, getDoc } from "firebase/firestore";
import { FaArrowLeft, FaMoneyBillWave, FaList, FaTrash, FaPlusCircle, FaFilePdf, FaUpload, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const CLOUD_NAME = "invoice-app";       
const API_KEY = "149469791857382";      
const API_SECRET = "CQJwV5ujAkgYO5BHYKMkGIITbMU";

const monthList = [
    { val: '01', name: 'January' }, { val: '02', name: 'February' }, { val: '03', name: 'March' },
    { val: '04', name: 'April' }, { val: '05', name: 'May' }, { val: '06', name: 'June' },
    { val: '07', name: 'July' }, { val: '08', name: 'August' }, { val: '09', name: 'September' },
    { val: '10', name: 'October' }, { val: '11', name: 'November' }, { val: '12', name: 'December' }
];

const BookingAccounts = () => {
    const navigate = useNavigate();
    
    const currentYear = new Date().getFullYear().toString();
    const [selYear, setSelYear] = useState(currentYear);
    const years = Array.from({length: 6}, (_, i) => (parseInt(currentYear) - 2 + i).toString());

    const [agents, setAgents] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState('');
    
    const [ledgerData, setLedgerData] = useState([]);
    const [openingBalance, setOpeningBalance] = useState(0);
    const [isEditingPrevBal, setIsEditingPrevBal] = useState(false);
    const [tempPrevBal, setTempPrevBal] = useState('');

    const [loading, setLoading] = useState(false);
    const [uploadingPdf, setUploadingPdf] = useState(null);

    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentForm, setPaymentForm] = useState({ date: new Date().toISOString().split('T')[0], amount: '', method: 'Cash', note: '' });
    const [savingPayment, setSavingPayment] = useState(false);

    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedMonthHistory, setSelectedMonthHistory] = useState(null);

    // --- Fetch initial agent list ---
    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const agentsSnap = await getDocs(query(collection(db, "booking_companies"), orderBy("name")));
                setAgents(agentsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (err) { console.error(err); }
        };
        fetchAgents();
    }, []);

    // --- Fetch Ledger Data for Selected Agent & Year ---
    const fetchLedgerData = useCallback(async () => {
        if (!selectedAgent) {
            setLedgerData([]);
            return;
        }
        setLoading(true);
        
        // Setup Empty Months Array
        const monthsData = monthList.map(m => ({
            monthVal: m.val,
            monthName: m.name,
            bill: 0,
            paid: 0,
            transactions: [],
            pdf: null,
            pdfId: null,
            publicId: null
        }));

        try {
            // 1. Fetch Opening Balance (Previous Due)
            const balDoc = await getDoc(doc(db, "booking_balances", `${selectedAgent}_${selYear}`));
            const prevBal = balDoc.exists() ? parseFloat(balDoc.data().balance || 0) : 0;
            setOpeningBalance(prevBal);

            // 2. Fetch Invoices for the Year
            const invoicesSnap = await getDocs(query(collection(db, "invoices")));
            invoicesSnap.docs.forEach(docSnap => {
                const inv = docSnap.data();
                let ym = inv.invoiceYearMonth;
                if (!ym && inv.invoiceDate) ym = inv.invoiceDate.slice(0, 7);
                
                if (ym && ym.startsWith(selYear) && inv.items) {
                    const parts = ym.split('-');
                    if (parts.length > 1) {
                        const mIndex = parseInt(parts[1]) - 1;
                        if (monthsData[mIndex]) {
                            inv.items.forEach(item => {
                                if (item.bookingAgentId === selectedAgent && item.bookingTotal > 0) {
                                    const amt = parseFloat(item.bookingTotal);
                                    monthsData[mIndex].bill += amt;
                                    monthsData[mIndex].transactions.push({
                                        id: `${docSnap.id}_${item.id}`,
                                        date: item.date || inv.invoiceDate,
                                        type: 'bill',
                                        amount: amt,
                                        note: `AWB: ${item.awbNo} (${item.weight} KG)`
                                    });
                                }
                            });
                        }
                    }
                }
            });

            // 3. Fetch Payments for the Year
            const ledgerSnap = await getDocs(query(collection(db, "booking_ledger")));
            ledgerSnap.docs.forEach(docSnap => {
                const pay = docSnap.data();
                if (pay.companyId === selectedAgent && pay.date && pay.date.startsWith(selYear)) {
                    const parts = pay.date.split('-');
                    if (parts.length > 1) {
                        const mIndex = parseInt(parts[1]) - 1;
                        if (monthsData[mIndex]) {
                            const amt = parseFloat(pay.paidAmount);
                            monthsData[mIndex].paid += amt;
                            monthsData[mIndex].transactions.push({
                                id: docSnap.id,
                                date: pay.date,
                                type: 'payment',
                                amount: amt,
                                method: pay.method,
                                note: pay.note
                            });
                        }
                    }
                }
            });

            // 4. Fetch PDFs
            const pdfsSnap = await getDocs(query(collection(db, "booking_monthly_pdfs")));
            pdfsSnap.docs.forEach(docSnap => {
                const data = docSnap.data();
                if (data.agentId === selectedAgent && data.year === selYear) {
                    const mIndex = parseInt(data.month) - 1;
                    if (monthsData[mIndex]) {
                        monthsData[mIndex].pdf = data.pdfUrl;
                        monthsData[mIndex].pdfId = docSnap.id;
                        monthsData[mIndex].publicId = data.publicId;
                    }
                }
            });

            // Sort transactions inside each month by date
            monthsData.forEach(m => {
                m.transactions.sort((a, b) => new Date(a.date) - new Date(b.date));
            });

        } catch (err) { 
            console.error("Data Error:", err); 
        }
        
        setLedgerData(monthsData);
        setLoading(false);
    }, [selectedAgent, selYear]);

    useEffect(() => {
        fetchLedgerData();
    }, [fetchLedgerData]);


    // --- Save Previous Balance ---
    const saveOpeningBalance = async () => {
        const val = parseFloat(tempPrevBal || 0);
        try {
            await setDoc(doc(db, "booking_balances", `${selectedAgent}_${selYear}`), { balance: val });
            setOpeningBalance(val);
            setIsEditingPrevBal(false);
        } catch (err) { alert("Failed to save balance."); }
    };


    // --- Cloudinary PDF Upload Logic ---
    const generateSignature = async (params) => {
        const sortedKeys = Object.keys(params).sort();
        const stringToSign = sortedKeys.map(key => `${key}=${params[key]}`).join('&') + API_SECRET;
        const encoder = new TextEncoder();
        const data = encoder.encode(stringToSign);
        const hashBuffer = await crypto.subtle.digest('SHA-1', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    };

    const uploadToCloudinary = async (file, prefix) => {
        const timestamp = Math.round((new Date()).getTime() / 1000);
        const public_id_custom = `${prefix}_${timestamp}`;
        const params = { timestamp: timestamp, folder: 'booking_pdfs', public_id: public_id_custom };
        const signature = await generateSignature(params);

        const formData = new FormData();
        formData.append("file", file);
        formData.append("api_key", API_KEY);
        formData.append("timestamp", timestamp);
        formData.append("folder", 'booking_pdfs');
        formData.append("public_id", public_id_custom);
        formData.append("signature", signature);

        const response = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/auto/upload`, { method: "POST", body: formData });
        const resData = await response.json();
        if (resData.error) throw new Error(resData.error.message);

        let url = resData.secure_url;
        if (resData.format === 'pdf') { url = url.replace('/upload/', '/upload/fl_attachment/'); }
        return { url: url, name: file.name, publicId: resData.public_id };
    };

    const deleteFromCloudinary = async (publicId) => {
        if (!publicId) return;
        const timestamp = Math.round((new Date()).getTime() / 1000);
        const params = { public_id: publicId, timestamp: timestamp };
        const signature = await generateSignature(params);
        const formData = new FormData();
        formData.append("public_id", publicId);
        formData.append("api_key", API_KEY);
        formData.append("timestamp", timestamp);
        formData.append("signature", signature);
        try {
            await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/destroy`, { method: "POST", body: formData });
            await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/raw/destroy`, { method: "POST", body: formData });
        } catch (err) {}
    };

    const handlePdfUpload = async (e, monthVal) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingPdf(monthVal);
        try {
            const { url, name, publicId } = await uploadToCloudinary(file, `${selectedAgent}_${selYear}_${monthVal}`);
            await addDoc(collection(db, "booking_monthly_pdfs"), {
                agentId: selectedAgent, year: selYear, month: monthVal, pdfUrl: url, pdfName: name, publicId: publicId, uploadedAt: Timestamp.now()
            });
            fetchLedgerData();
        } catch (err) { alert("Upload failed."); }
        setUploadingPdf(null);
    };

    const handleDeletePdf = async (pdfId, publicId) => {
        if (!window.confirm("Delete this PDF statement?")) return;
        try {
            if (publicId) await deleteFromCloudinary(publicId);
            await deleteDoc(doc(db, "booking_monthly_pdfs", pdfId));
            fetchLedgerData();
        } catch (err) { alert("Failed to delete PDF."); }
    };


    // --- Payment Logic ---
    const savePayment = async (e) => {
        e.preventDefault();
        if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) return alert("Enter a valid amount.");
        setSavingPayment(true);
        const agentObj = agents.find(a => a.id === selectedAgent);
        try {
            await addDoc(collection(db, "booking_ledger"), {
                date: paymentForm.date, companyId: selectedAgent, companyName: agentObj.name,
                paidAmount: parseFloat(paymentForm.amount), method: paymentForm.method, note: paymentForm.note, timestamp: Timestamp.now()
            });
            setShowPaymentModal(false);
            setPaymentForm({ date: new Date().toISOString().split('T')[0], amount: '', method: 'Cash', note: '' });
            fetchLedgerData();
            alert("Payment added successfully!");
        } catch (error) { alert("Failed to save payment."); }
        setSavingPayment(false);
    };

    const handleDeletePayment = async (id) => {
        if(window.confirm("Are you sure you want to delete this payment record?")) {
            try {
                await deleteDoc(doc(db, "booking_ledger", id));
                fetchLedgerData();
                setShowHistoryModal(false);
            } catch (error) { alert("Error deleting payment."); }
        }
    };


    // --- Utils ---
    const formatMoney = (n) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

    const container = { padding: '20px', background: '#f4f6f8', minHeight: '100vh', fontFamily: "'Segoe UI', sans-serif" };
    const card = { background: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)', marginBottom: '20px' };
    const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '14px' };
    const thStyle = { background: '#2c3e50', padding: '12px 10px', textAlign: 'left', color: 'white', fontWeight: 'bold', border: '1px solid #34495e' };
    const tdStyle = { padding: '12px 10px', borderBottom: '1px solid #eee', borderRight: '1px solid #eee', color: '#333', verticalAlign: 'middle' };
    const inputStyle = { padding: '8px', border: '1px solid #ddd', borderRadius: '4px', width: '100%', outline: 'none' };
    const btn = { padding: '8px 15px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' };
    const iconBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', padding: '6px', fontSize: '15px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', transition: '0.2s', margin: '0 2px' };

    let runningBalance = openingBalance; 

    return (
        <div style={container}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <button onClick={() => navigate('/dashboard/booking-report')} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'none', border: 'none', color: '#3498db', fontWeight: 'bold', cursor: 'pointer', fontSize: '15px', padding: 0, marginBottom: '10px' }}>
                        <FaArrowLeft /> Back to Booking Report
                    </button>
                    <h2 style={{ color: '#2c3e50', margin: 0 }}>Accountant Ledger</h2>
                </div>
            </div>

            {/* Filter Section */}
            <div style={{ ...card, display: 'flex', gap: '15px', alignItems: 'center', background: '#ecf0f1', padding: '15px 20px' }}>
                <div style={{ flex: 1, display: 'flex', gap: '15px', alignItems: 'center' }}>
                    <span style={{ fontWeight: 'bold', color: '#555', fontSize: '15px' }}>Select Agent:</span>
                    <select value={selectedAgent} onChange={(e) => setSelectedAgent(e.target.value)} style={{ ...inputStyle, width: '250px', fontWeight: 'bold' }}>
                        <option value="">-- Choose Agent --</option>
                        {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>

                    <span style={{ fontWeight: 'bold', color: '#555', fontSize: '15px', marginLeft: '10px' }}>Year:</span>
                    <select value={selYear} onChange={(e) => setSelYear(e.target.value)} style={{ ...inputStyle, width: '100px', fontWeight: 'bold' }}>
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                
                {selectedAgent && (
                    <button onClick={() => setShowPaymentModal(true)} style={{ ...btn, background: '#27ae60', color: 'white' }}>
                        <FaPlusCircle /> Add Payment
                    </button>
                )}
            </div>

            {/* LEDGER TABLE VIEW */}
            {!selectedAgent ? (
                <div style={{...card, textAlign: 'center', padding: '40px', color: '#7f8c8d'}}>
                    <p style={{fontSize: '18px', fontWeight: 'bold'}}>Please select an Agent to view their Ledger.</p>
                </div>
            ) : loading ? (
                <div style={{...card, textAlign: 'center', padding: '40px'}}>
                    <p style={{fontSize: '16px', fontWeight: 'bold'}}>Loading Ledger Data...</p>
                </div>
            ) : (
                <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                    
                    <div style={{background: '#34495e', color: 'white', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <h3 style={{margin: 0}}>{agents.find(a=>a.id===selectedAgent)?.name} - Ledger ({selYear})</h3>
                    </div>

                    <table style={tableStyle}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Month</th>
                                <th style={{...thStyle, textAlign: 'right', background: '#c0392b'}}>Bill Amount (Dr)</th>
                                <th style={{...thStyle, textAlign: 'right', background: '#27ae60'}}>Paid Amount (Cr)</th>
                                <th style={{...thStyle, textAlign: 'right', background: '#2980b9'}}>Balance</th>
                                <th style={{...thStyle, textAlign: 'center'}}>Statement (PDF)</th>
                                <th style={{...thStyle, textAlign: 'center'}}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Row 0: Opening Balance */}
                            <tr style={{ background: '#fdf8e3' }}>
                                <td style={{ ...tdStyle, fontWeight: 'bold' }}>Opening Balance (Previous Due)</td>
                                <td style={tdStyle}></td>
                                <td style={tdStyle}></td>
                                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '15px', color: openingBalance > 0 ? '#e74c3c' : (openingBalance < 0 ? '#27ae60' : '#333') }}>
                                    {isEditingPrevBal ? (
                                        <div style={{display: 'flex', justifyContent: 'flex-end', gap: '5px'}}>
                                            <input type="number" value={tempPrevBal} onChange={e=>setTempPrevBal(e.target.value)} style={{...inputStyle, width: '100px', padding: '4px'}} />
                                            <button onClick={saveOpeningBalance} style={{...iconBtnStyle, background: '#27ae60', color: 'white'}}><FaSave/></button>
                                            <button onClick={()=>setIsEditingPrevBal(false)} style={{...iconBtnStyle, background: '#e74c3c', color: 'white'}}><FaTimes/></button>
                                        </div>
                                    ) : (
                                        <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px'}}>
                                            <span>৳ {formatMoney(Math.abs(openingBalance))} {openingBalance > 0 ? 'Dr' : (openingBalance < 0 ? 'Cr' : '')}</span>
                                            <button onClick={()=>{setTempPrevBal(openingBalance); setIsEditingPrevBal(true);}} style={{...iconBtnStyle, background: '#eee', color: '#555'}} title="Edit Opening Balance"><FaEdit/></button>
                                        </div>
                                    )}
                                </td>
                                <td style={tdStyle}></td>
                                <td style={tdStyle}></td>
                            </tr>

                            {/* Row 1 to 12: Months */}
                            {ledgerData.map((m, index) => {
                                runningBalance = runningBalance + m.bill - m.paid;
                                const hasData = m.bill > 0 || m.paid > 0 || m.pdf;

                                return (
                                    <tr key={index} style={{ background: 'white', transition: '0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'} onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                                        <td style={{ ...tdStyle, fontWeight: 'bold' }}>{m.monthName}</td>
                                        <td style={{ ...tdStyle, textAlign: 'right', color: '#e74c3c', fontWeight: 'bold' }}>
                                            {m.bill > 0 ? `৳ ${formatMoney(m.bill)}` : '-'}
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'right', color: '#27ae60', fontWeight: 'bold' }}>
                                            {m.paid > 0 ? `৳ ${formatMoney(m.paid)}` : '-'}
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: runningBalance > 0 ? '#e74c3c' : (runningBalance < 0 ? '#27ae60' : '#333') }}>
                                            ৳ {formatMoney(Math.abs(runningBalance))} {runningBalance > 0 ? 'Dr' : (runningBalance < 0 ? 'Cr' : '')}
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            {m.pdf ? (
                                                <div style={{ display: 'flex', justifyContent: 'center', gap: '5px' }}>
                                                    <a href={m.pdf} target="_blank" rel="noreferrer" style={{...iconBtnStyle, background: '#fce4e4'}} title="View PDF">
                                                        <FaFilePdf color="#e74c3c" /> View
                                                    </a>
                                                    <button onClick={() => handleDeletePdf(m.pdfId, m.publicId)} style={{...iconBtnStyle, background: '#f0f0f0'}} title="Delete PDF">
                                                        <FaTrash color="#95a5a6" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <label style={{ ...btn, background: '#ecf0f1', color: '#2c3e50', cursor: 'pointer', margin: '0 auto', width: 'fit-content', fontSize: '12px' }} title="Upload Month PDF">
                                                    {uploadingPdf === m.monthVal ? 'Uploading...' : <><FaUpload color="#3498db" /> Upload</>}
                                                    <input type="file" style={{ display: 'none' }} onChange={(e) => handlePdfUpload(e, m.monthVal)} accept="application/pdf,image/*" />
                                                </label>
                                            )}
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                                                <button onClick={() => setShowPaymentModal(true)} style={{...btn, background: '#3498db', color: 'white', fontSize: '11px', padding: '5px 10px'}} title="Add Payment">
                                                    <FaMoneyBillWave /> Pay
                                                </button>
                                                <button disabled={!hasData} onClick={() => { setSelectedMonthHistory({...m, openingForMonth: runningBalance - m.bill + m.paid}); setShowHistoryModal(true); }} style={{...btn, background: hasData ? '#f39c12' : '#ecf0f1', color: hasData ? 'white' : '#bdc3c7', fontSize: '11px', padding: '5px 10px', cursor: hasData ? 'pointer' : 'not-allowed'}} title="View Month Details">
                                                    <FaList /> Details
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        {/* Grand Total */}
                        <tfoot>
                            <tr style={{ background: '#ecf0f1' }}>
                                <th style={{ ...thStyle, background: '#bdc3c7', color: '#2c3e50', textAlign: 'right' }}>TOTAL FOR {selYear}:</th>
                                <th style={{ ...thStyle, background: '#bdc3c7', color: '#c0392b', textAlign: 'right' }}>
                                    ৳ {formatMoney(ledgerData.reduce((acc, curr) => acc + curr.bill, 0))}
                                </th>
                                <th style={{ ...thStyle, background: '#bdc3c7', color: '#27ae60', textAlign: 'right' }}>
                                    ৳ {formatMoney(ledgerData.reduce((acc, curr) => acc + curr.paid, 0))}
                                </th>
                                <th colSpan="3" style={{...thStyle, background: '#bdc3c7'}}></th>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}


            {/* ================= MODALS ================= */}

            {/* 1. Add Payment Modal */}
            {showPaymentModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', padding: '30px', borderRadius: '10px', width: '400px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                        <h3 style={{ marginTop: 0, color: '#2c3e50', borderBottom: '2px solid #ecf0f1', paddingBottom: '10px' }}>Add Payment</h3>
                        <form onSubmit={savePayment} style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '15px' }}>
                            <div>
                                <label style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>Agent Name</label>
                                <input type="text" value={agents.find(a=>a.id===selectedAgent)?.name} disabled style={{ ...inputStyle, background: '#f8f9fa', fontWeight: 'bold' }} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <div>
                                    <label style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>Payment Date</label>
                                    <input type="date" required value={paymentForm.date} onChange={e => setPaymentForm({ ...paymentForm, date: e.target.value })} style={inputStyle} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>Payment Method</label>
                                    <select value={paymentForm.method} onChange={e => setPaymentForm({ ...paymentForm, method: e.target.value })} style={inputStyle}>
                                        <option>Cash</option><option>Bank</option><option>Mobile Banking</option><option>Cheque</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>Amount (৳)</label>
                                <input type="number" required placeholder="0.00" value={paymentForm.amount} onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })} style={{ ...inputStyle, fontSize: '18px', fontWeight: 'bold', color: '#27ae60' }} />
                            </div>
                            <div>
                                <label style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>Note / Reference</label>
                                <input type="text" placeholder="Optional details..." value={paymentForm.note} onChange={e => setPaymentForm({ ...paymentForm, note: e.target.value })} style={inputStyle} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                                <button type="button" onClick={() => setShowPaymentModal(false)} style={{ ...btn, background: '#95a5a6', color: 'white' }}>Cancel</button>
                                <button type="submit" disabled={savingPayment} style={{ ...btn, background: '#27ae60', color: 'white' }}>
                                    {savingPayment ? 'Saving...' : 'Save Payment'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* 2. Detailed Monthly Ledger Modal */}
            {showHistoryModal && selectedMonthHistory && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', padding: '0', borderRadius: '10px', width: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
                        
                        {/* Header */}
                        <div style={{ background: '#34495e', color: 'white', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h3 style={{ margin: 0 }}>{agents.find(a=>a.id===selectedAgent)?.name}</h3>
                                <p style={{ margin: '5px 0 0', fontSize: '13px', color: '#f1c40f' }}>Transactions for {selectedMonthHistory.monthName} {selYear}</p>
                            </div>
                            <button onClick={() => setShowHistoryModal(false)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
                        </div>

                        {/* Professional Summary Boxes */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', background: '#f8f9fa', padding: '15px', borderBottom: '1px solid #ddd', gap: '10px' }}>
                            <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', borderLeft: '4px solid #7f8c8d', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                <span style={{ fontSize: '11px', color: '#7f8c8d', fontWeight: 'bold', textTransform: 'uppercase' }}>Opening Balance</span>
                                <h3 style={{ margin: '5px 0 0', color: '#2c3e50', fontSize: '16px' }}>৳ {formatMoney(Math.abs(selectedMonthHistory.openingForMonth))}</h3>
                            </div>
                            <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', borderLeft: '4px solid #e74c3c', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                <span style={{ fontSize: '11px', color: '#7f8c8d', fontWeight: 'bold', textTransform: 'uppercase' }}>Bill Amount (+)</span>
                                <h3 style={{ margin: '5px 0 0', color: '#e74c3c', fontSize: '16px' }}>৳ {formatMoney(selectedMonthHistory.bill)}</h3>
                            </div>
                            <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', borderLeft: '4px solid #27ae60', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                <span style={{ fontSize: '11px', color: '#7f8c8d', fontWeight: 'bold', textTransform: 'uppercase' }}>Paid Amount (-)</span>
                                <h3 style={{ margin: '5px 0 0', color: '#27ae60', fontSize: '16px' }}>৳ {formatMoney(selectedMonthHistory.paid)}</h3>
                            </div>
                            <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', borderLeft: '4px solid #2980b9', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', backgroundColor: '#f0f8ff' }}>
                                <span style={{ fontSize: '11px', color: '#2980b9', fontWeight: 'bold', textTransform: 'uppercase' }}>Closing Balance</span>
                                <h3 style={{ margin: '5px 0 0', color: '#2980b9', fontSize: '18px' }}>৳ {formatMoney(Math.abs(selectedMonthHistory.openingForMonth + selectedMonthHistory.bill - selectedMonthHistory.paid))}</h3>
                            </div>
                        </div>

                        {/* Transactions Table with Running Balance */}
                        <div style={{ overflowY: 'auto', padding: '15px', flex: 1 }}>
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        <th style={{ ...thStyle, background: '#ecf0f1', color: '#333' }}>Date</th>
                                        <th style={{ ...thStyle, background: '#ecf0f1', color: '#333' }}>Description</th>
                                        <th style={{ ...thStyle, background: '#ecf0f1', color: '#e74c3c', textAlign: 'right' }}>Bill (Dr)</th>
                                        <th style={{ ...thStyle, background: '#ecf0f1', color: '#27ae60', textAlign: 'right' }}>Paid (Cr)</th>
                                        <th style={{ ...thStyle, background: '#ecf0f1', color: '#2980b9', textAlign: 'right' }}>Running Balance</th>
                                        <th style={{ ...thStyle, background: '#ecf0f1', color: '#333', textAlign: 'center' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        let runningBalForTable = selectedMonthHistory.openingForMonth;
                                        return selectedMonthHistory.transactions.length > 0 ? selectedMonthHistory.transactions.map((t, idx) => {
                                            runningBalForTable = runningBalForTable + (t.type === 'bill' ? t.amount : 0) - (t.type === 'payment' ? t.amount : 0);
                                            return (
                                                <tr key={idx} style={{ background: t.type === 'payment' ? '#f0f9f0' : '#fff', borderBottom: '1px solid #eee' }}>
                                                    <td style={tdStyle}>{t.date}</td>
                                                    <td style={{ ...tdStyle, textAlign: 'left' }}>
                                                        {t.type === 'bill' ? (
                                                            <span><b>Booking:</b> {t.note}</span>
                                                        ) : (
                                                            <span style={{ color: '#27ae60' }}><b>Payment ({t.method}):</b> {t.note || 'N/A'}</span>
                                                        )}
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: '#e74c3c' }}>
                                                        {t.type === 'bill' ? formatMoney(t.amount) : '-'}
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: '#27ae60' }}>
                                                        {t.type === 'payment' ? formatMoney(t.amount) : '-'}
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: runningBalForTable > 0 ? '#e74c3c' : '#2980b9' }}>
                                                        {formatMoney(Math.abs(runningBalForTable))} {runningBalForTable > 0 ? 'Dr' : 'Cr'}
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                        {t.type === 'payment' && (
                                                            <button onClick={() => handleDeletePayment(t.id)} title="Delete Payment" style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', padding: '5px' }}>
                                                                <FaTrash />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr><td colSpan="6" style={{ padding: '20px', textAlign: 'center' }}>No transactions found for this month.</td></tr>
                                        );
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingAccounts;